import { describe, it, expect, vi, beforeEach, afterEach } from 'vitest';
import { createTrackingProperties } from './tracking';

describe('Unit | Utility | mandates | tracking | createTrackingProperties', () => {
  const originalUserAgent = navigator.userAgent;

  beforeEach(() => {
    vi.clearAllMocks();
  });

  afterEach(() => {
    vi.restoreAllMocks();
    Object.defineProperty(navigator, 'userAgent', {
      value: originalUserAgent,
      writable: true,
      configurable: true,
    });
  });

  describe('Platform detection', () => {
    it('detects mobile platform correctly', () => {
      Object.defineProperty(navigator, 'userAgent', {
        value:
          'Mozilla/5.0 (iPhone; CPU iPhone OS 14_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/14.0.3 Mobile/15E148 Safari/604.1',
        writable: true,
        configurable: true,
      });

      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: false,
        addressWasPrefilled: false,
        scheduleType: 'one_off',
        scheduleTotalCollectionCount: 0,
      });

      const properties = getTrackingProperties(false);

      expect(properties.platform).toBe('mobile');
    });

    it('detects web platform correctly', () => {
      Object.defineProperty(navigator, 'userAgent', {
        value: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:95.0) Gecko/20100101 Firefox/95.0',
        writable: true,
        configurable: true,
      });

      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: false,
        addressWasPrefilled: false,
        scheduleType: 'one_off',
        scheduleTotalCollectionCount: 0,
      });

      const properties = getTrackingProperties(false);

      expect(properties.platform).toBe('web');
    });
  });

  describe('One-off mandate', () => {
    it('returns correct properties for one-off mandate without prefilled address', () => {
      Object.defineProperty(navigator, 'userAgent', {
        value: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:95.0) Gecko/20100101 Firefox/95.0',
        writable: true,
        configurable: true,
      });

      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: false,
        addressWasPrefilled: false,
        scheduleType: 'one_off',
        scheduleTotalCollectionCount: 0,
      });

      const properties = getTrackingProperties(false);

      expect(properties).toStrictEqual({
        collection_type: 'one-off',
        platform: 'web',
        address_prefilled: false,
      });
    });

    it('returns correct properties for one-off mandate with prefilled address', () => {
      Object.defineProperty(navigator, 'userAgent', {
        value:
          'Mozilla/5.0 (iPhone; CPU iPhone OS 14_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/14.0.3 Mobile/15E148 Safari/604.1',
        writable: true,
        configurable: true,
      });

      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: false,
        addressWasPrefilled: true,
        scheduleType: 'one_off',
        scheduleTotalCollectionCount: 0,
      });

      const properties = getTrackingProperties(false);

      expect(properties).toStrictEqual({
        collection_type: 'one-off',
        platform: 'mobile',
        address_prefilled: true,
      });
    });

    it('does not include frequency or duration for one-off mandate', () => {
      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: false,
        addressWasPrefilled: false,
        scheduleType: 'one_off',
        scheduleTotalCollectionCount: 0,
      });

      const properties = getTrackingProperties(true);

      expect(properties).not.toHaveProperty('frequency');
      expect(properties).not.toHaveProperty('duration');
    });
  });

  describe('Recurring mandate', () => {
    it('returns correct properties for recurring mandate with monthly frequency', () => {
      Object.defineProperty(navigator, 'userAgent', {
        value: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:95.0) Gecko/20100101 Firefox/95.0',
        writable: true,
        configurable: true,
      });

      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: true,
        addressWasPrefilled: true,
        scheduleType: 'monthly',
        scheduleTotalCollectionCount: 10,
      });

      const properties = getTrackingProperties(false);

      expect(properties).toStrictEqual({
        collection_type: 'recurring',
        platform: 'web',
        address_prefilled: true,
        frequency: 'monthly',
      });
    });

    it('includes duration when shouldIncludeDuration is true and count > 0', () => {
      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: true,
        addressWasPrefilled: false,
        scheduleType: 'monthly',
        scheduleTotalCollectionCount: 10,
      });

      const properties = getTrackingProperties(true);

      expect(properties.collection_type).toBe('recurring');
      expect(properties.platform).toMatch(/^(?:mobile|web)$/);
      expect(properties.address_prefilled).toBe(false);
      expect(properties.frequency).toBe('monthly');
      expect(properties.duration).toBe('number_payments');
    });

    it('includes duration as "no_limit" when scheduleTotalCollectionCount is 0', () => {
      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: true,
        addressWasPrefilled: false,
        scheduleType: 'monthly',
        scheduleTotalCollectionCount: 0,
      });

      const properties = getTrackingProperties(true);

      expect(properties.collection_type).toBe('recurring');
      expect(properties.platform).toMatch(/^(?:mobile|web)$/);
      expect(properties.address_prefilled).toBe(false);
      expect(properties.frequency).toBe('monthly');
      expect(properties.duration).toBe('no_limit');
    });

    it('does not include duration when shouldIncludeDuration is false', () => {
      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: true,
        addressWasPrefilled: false,
        scheduleType: 'monthly',
        scheduleTotalCollectionCount: 10,
      });

      const properties = getTrackingProperties(false);

      expect(properties).not.toHaveProperty('duration');
      expect(properties.frequency).toBe('monthly');
    });

    it('handles custom schedule type', () => {
      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: true,
        addressWasPrefilled: true,
        scheduleType: 'custom',
        scheduleTotalCollectionCount: 5,
      });

      const properties = getTrackingProperties(true);

      expect(properties.collection_type).toBe('recurring');
      expect(properties.frequency).toBe('custom');
      expect(properties.duration).toBe('number_payments');
    });
  });

  describe('Mobile user agents', () => {
    it.each([
      [
        'Android',
        'Mozilla/5.0 (Linux; Android 10; SM-G973F) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.120 Mobile Safari/537.36',
      ],
      [
        'iPhone',
        'Mozilla/5.0 (iPhone; CPU iPhone OS 14_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/14.0.3 Mobile/15E148 Safari/604.1',
      ],
      [
        'iPad',
        'Mozilla/5.0 (iPad; CPU OS 14_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/14.0.3 Mobile/15E148 Safari/604.1',
      ],
      [
        'BlackBerry',
        'Mozilla/5.0 (BlackBerry; U; BlackBerry 9900; en) AppleWebKit/534.11+ (KHTML, like Gecko) Version/7.1.0.346 Mobile Safari/534.11+',
      ],
    ])('detects %s as mobile', (_name, userAgent) => {
      Object.defineProperty(navigator, 'userAgent', {
        value: userAgent,
        writable: true,
        configurable: true,
      });

      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: false,
        addressWasPrefilled: false,
        scheduleType: 'one_off',
        scheduleTotalCollectionCount: 0,
      });

      const properties = getTrackingProperties(false);

      expect(properties.platform).toBe('mobile');
    });
  });
});
